import React, { useEffect, useRef, useState } from 'react';
import { useLanggraphChat } from '../context/LanggraphChatContext';
import { useLanguage } from '../context/LanguageContext';
import { LangGraphMessage } from '../types';
import Header from '../components/layout/Header';
import ChatMessage from '../components/chat/ChatMessage';
import ProgressIndicator from '../components/chat/ProgressIndicator';
import ChatInput from '../components/chat/ChatInput';

// Mock search suggestions utility function
const getSearchSuggestions = (labels: any) => [
  { id: '1', text: labels.searchSuggestion1, category: 'trends' },
  { id: '2', text: labels.searchSuggestion2, category: 'business' },
  { id: '3', text: labels.searchSuggestion3, category: 'productivity' },
];

const ChatPage: React.FC = () => {
  const { 
    messages, 
    isProcessing, 
    sendUserMessage,
  } = useLanggraphChat();
  const { labels } = useLanguage();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState(() => getSearchSuggestions(labels));
  
  useEffect(() => {
    setSuggestions(getSearchSuggestions(labels));
  }, [labels]);
  
  // Helper function to get content from LangGraph message
  const getMessageContent = (message: LangGraphMessage) => {
    if (!message.content) return '';
    if (Array.isArray(message.content)) {
      return message.content
        .filter((chunk) => chunk.type === 'text')
        .map((chunk) => chunk.text)
        .join('');
    }
    return message.content;
  };
  
  // Filter messages to show only meaningful content
  const filteredMessages = messages.filter((message: LangGraphMessage, index: number) => {
    // Hide the first message (initialization message from user)
    if (index === 0 && message.type === 'human') {
      return false;
    }
    
    // For AI messages, check if it's from the main agent or research agent
    if (message.type === 'ai') {
      const agentName = message.name;
      // Hide research agent messages that contain "Transferring back to supervisor"
      if (agentName === 'research_agent' && getMessageContent(message).includes('Transferring back to supervisor')) {
        return false;
      }
      // Show main agent (agent/supervisor) and research agent messages
      return agentName === 'agent' || agentName === 'supervisor' || agentName === 'research_agent';
    }
    
    return message.type === 'human';
  }).filter((message: LangGraphMessage) => {
    // Filter out messages with empty content
    const content = getMessageContent(message);
    return content && content.trim() !== '';
  });
  
  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredMessages, isProcessing]);

  const handleSendMessage = (content: string) => {
    // Send the user message
    sendUserMessage(content);
  };
  
  // Only show suggestions when there is exactly 1 message (agent's response after introduction)
  const shouldShowSuggestions = filteredMessages.length === 1;
  
  return (
    <div className="flex flex-col h-screen bg-primary-50">
      <Header />
      
      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {filteredMessages.map((message, index) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                onSendMessage={handleSendMessage}
                isLastMessage={index === filteredMessages.length - 1}
              />
            ))}
            {isProcessing && (
              <ProgressIndicator messages={messages} />
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </main>
      
      <div className="p-4 border-t border-primary-200 bg-white shadow-sm">
        <ChatInput 
          onSendMessage={handleSendMessage} 
          isDisabled={isProcessing}
          suggestions={shouldShowSuggestions ? suggestions : []}
        />
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-primary-100 py-3 px-4">
        <div className="max-w-4xl mx-auto flex justify-center">
          <a 
            href="https://ai-growth-minds.dk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:text-primary-800 transition-colors flex items-center gap-1"
          >
            Powered by 
            <span className="font-semibold">AI Growth Minds</span>
          </a>
        </div>
      </footer>
    </div>
  );
};

export default ChatPage;